import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  MapPin, Check, Pencil, Search, ExternalLink, Wind, Droplets,
  Thermometer, Loader2, Compass, BookOpen, Newspaper, X,
} from "lucide-react";
import { usePlace } from "@/lib/LocationContext";
import { useLocalBundle } from "@/lib/useLocalBundle";
import { weatherInfo } from "@/lib/weather";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export function placeLine(place) {
  return [place?.city, place?.region, place?.country].filter(Boolean).join(" · ");
}

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Search-and-pick panel used by the "Change" function. */
export function PlacePicker({ onPick, autofocus = true, testid = "place-picker" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/geo/search`, { params: { q: query } });
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div data-testid={testid}>
      <label className="flex items-center gap-2 border-b border-[color:var(--sepia)]/50 pb-2">
        <Search size={14} className="text-[color:var(--sepia)] shrink-0" />
        <input
          data-testid={`${testid}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus={autofocus}
          placeholder="Search a town, a city, a village…"
          className="w-full bg-transparent focus:outline-none placeholder:text-[color:var(--sepia)]/50 text-[16px]"
        />
        {searching && <Loader2 size={14} className="animate-spin text-[color:var(--sepia)]" />}
      </label>
      {results.length > 0 && (
        <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-[color:var(--sepia)]/15" data-testid={`${testid}-results`}>
          {results.map((r, i) => (
            <li key={`${r.city}-${r.region}-${i}`}>
              <button
                onClick={() => onPick(r)}
                data-testid={`${testid}-result-${i}`}
                className="w-full text-left px-2 py-2.5 hover:bg-[color:var(--parchment-2)]/60 flex items-center justify-between gap-3"
              >
                <span className="font-serif-display text-lg leading-tight">{r.city}</span>
                <span className="text-xs text-[color:var(--ink-soft)] text-right">
                  {[r.region, r.country].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {q.trim().length >= 2 && !searching && results.length === 0 && (
        <p className="mt-3 text-sm font-italic-serif italic text-[color:var(--ink-soft)]">
          No places found — try a nearby town.
        </p>
      )}
    </div>
  );
}

/** The City — Region — Country bar with Set / Change functions. */
function LocationBar() {
  const { place, pinned, detecting, pinPlace, resetPlace } = usePlace();
  const [changing, setChanging] = useState(false);

  return (
    <div className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] shadow-[0_16px_44px_-24px_rgba(43,28,15,0.45)]" data-testid="location-bar">
      <div className="px-5 md:px-8 py-5 flex flex-wrap items-center gap-x-6 gap-y-4 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <MapPin size={18} className="text-[color:var(--rust)] shrink-0" />
          {detecting ? (
            <span className="font-italic-serif italic text-[color:var(--ink-soft)]" data-testid="location-detecting">
              Finding where you are…
            </span>
          ) : place ? (
            <span className="font-serif-display text-xl md:text-2xl truncate" data-testid="location-line">
              {place.city}
              {place.region && <span className="text-[color:var(--ink-soft)]"> — {place.region}</span>}
              {place.country && <span className="text-[color:var(--sepia)]"> — {place.country}</span>}
            </span>
          ) : (
            <span className="font-italic-serif italic text-[color:var(--ink-soft)]" data-testid="location-unknown">
              We couldn't place you — tell us where your story begins.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {place && !pinned && (
            <button
              data-testid="location-set"
              onClick={() => pinPlace(place)}
              className="btn-wood !py-2 !px-4 !text-[12px]"
            >
              <Check size={13} /> Set
            </button>
          )}
          {place && pinned && (
            <span className="inline-flex items-center gap-1 text-[11px] font-heritage-caps tracking-[0.14em] text-[color:var(--sepia)]" data-testid="location-saved">
              <Check size={12} /> Saved
            </span>
          )}
          <button
            data-testid="location-change"
            onClick={() => setChanging((v) => !v)}
            className="btn-ghost !py-2 !px-4 !text-[12px]"
          >
            {changing ? <X size={13} /> : <Pencil size={13} />} {changing ? "Close" : "Change"}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {(changing || (!detecting && !place)) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease }}
            className="overflow-hidden border-t border-[color:var(--sepia)]/20"
          >
            <div className="px-5 md:px-8 py-5">
              <PlacePicker
                onPick={(p) => {
                  pinPlace(p);
                  setChanging(false);
                }}
              />
              {!pinned && place && (
                <button
                  onClick={resetPlace}
                  data-testid="location-redetect"
                  className="mt-3 inline-flex items-center gap-1 text-xs link-underline text-[color:var(--ink-soft)]"
                >
                  <Compass size={12} /> Detect my location again
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WeatherPanel({ weather, place, loading }) {
  if (loading && !weather) {
    return (
      <div className="border border-[color:var(--sepia)]/25 p-8 flex items-center justify-center text-[color:var(--ink-soft)]" data-testid="weather-loading">
        <Loader2 size={18} className="animate-spin mr-2" /> Reading the sky…
      </div>
    );
  }
  if (!weather || weather.temperature == null) return null;
  const info = weatherInfo(weather.weather_code, weather.is_day);
  const label = weather.label || info.label;
  const Icon = weather.label ? Thermometer : info.Icon;
  return (
    <section className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-6 md:p-8" data-testid="weather-panel">
      <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
        <Thermometer size={12} /> The weather over {place.city}
      </div>
      <div className="flex items-end justify-between gap-4">
        <div className="font-serif-display leading-none" style={{ fontSize: "clamp(64px, 8vw, 120px)" }} data-testid="weather-temp">
          {Math.round(weather.temperature)}°
        </div>
        <div className="text-right pb-2">
          <Icon size={36} className="ml-auto text-[color:var(--sepia)]" />
          <div className="mt-2 font-italic-serif italic text-lg text-[color:var(--ink-soft)]" data-testid="weather-label">{label}</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[color:var(--sepia)]/20 pt-4 text-sm">
        <div>
          <div className="overline text-[10px] text-[color:var(--ink-soft)]">Feels like</div>
          <div className="mt-1 font-medium">{Math.round(weather.feels_like)}°</div>
        </div>
        <div>
          <div className="overline text-[10px] text-[color:var(--ink-soft)] flex items-center gap-1"><Droplets size={10} /> Humidity</div>
          <div className="mt-1 font-medium">{weather.humidity}%</div>
        </div>
        <div>
          <div className="overline text-[10px] text-[color:var(--ink-soft)] flex items-center gap-1"><Wind size={10} /> Wind</div>
          <div className="mt-1 font-medium">{Math.round(weather.wind_speed)} km/h</div>
        </div>
      </div>
      {(weather.days || []).filter((d) => d.tmax != null).length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          {weather.days.filter((d) => d.tmax != null).slice(0, 3).map((d, i) => {
            const info = weatherInfo(d.code, 1);
            return (
              <div key={d.date} className="border border-[color:var(--sepia)]/20 px-3 py-3 text-center" data-testid={`weather-day-${i}`}>
                <div className="overline text-[9px] text-[color:var(--ink-soft)]">
                  {i === 0 ? "Today" : new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <info.Icon size={16} className="mx-auto mt-2 text-[color:var(--sepia)]" />
                <div className="mt-2 text-sm tabular-nums">
                  {Math.round(d.tmax)}° <span className="text-[color:var(--ink-soft)]">{Math.round(d.tmin)}°</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4 numeric-index text-[color:var(--ink-soft)]">Free open weather sources · refreshed regularly</div>
    </section>
  );
}

function ArticlePanel({ article, place, loading }) {
  if (loading && !article) {
    return (
      <div className="border border-[color:var(--sepia)]/25 p-8 flex items-center justify-center text-[color:var(--ink-soft)]">
        <Loader2 size={18} className="animate-spin mr-2" /> Unfolding the local story…
      </div>
    );
  }
  if (!article) {
    return (
      <section className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-6 md:p-8">
        <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
          <BookOpen size={12} /> The story of this place
        </div>
        <p className="font-italic-serif italic text-lg text-[color:var(--ink-soft)] leading-[1.65]">
          Every family story begins somewhere — and {place.city || "this corner of the world"} is one of those
          somewheres. Records, cemeteries and old photographs from this region are waiting in the archive.
        </p>
        <Link to="/family-trees" className="btn-ghost mt-6 !py-2 !px-4 !text-xs inline-flex">Begin a family tree</Link>
      </section>
    );
  }
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-6 md:p-8"
      data-testid="local-article"
    >
      <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
        <BookOpen size={12} /> The story of this place
      </div>
      <div className="flex gap-5 items-start">
        {article.thumbnail && (
          <div className="vintage-frame w-28 shrink-0 hidden sm:block">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" style={{ filter: "sepia(20%)" }} />
            </div>
          </div>
        )}
        <div className="min-w-0">
          <h2 className="heritage-title text-2xl md:text-3xl leading-tight">{article.title}</h2>
          {article.description && (
            <div className="mt-1 font-italic-serif italic text-[color:var(--ink-soft)]">{article.description}</div>
          )}
          <p className="mt-4 text-[16px] leading-[1.7] text-[color:var(--ink)]">{article.extract}</p>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="local-article-link"
              className="mt-4 inline-flex items-center gap-1 text-sm link-underline text-[color:var(--rust)]"
            >
              Continue reading on Wikipedia <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export function NewsList({ news, compact = false, testid = "news-list" }) {
  if (!news?.length) return null;
  const items = compact ? news.slice(0, 3) : news;
  return (
    <ol className="divide-y divide-[color:var(--sepia)]/15" data-testid={testid}>
      {items.map((n, i) => (
        <li key={i}>
          <a
            href={n.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-item-${i}`}
            className="group flex items-baseline gap-4 py-3.5"
          >
            <span className="numeric-index text-[color:var(--sepia)] shrink-0 w-7">{String(i + 1).padStart(2, "0")}</span>
            <span className="min-w-0 flex-1">
              <span className="font-serif-display text-lg leading-snug group-hover:text-[color:var(--rust)] transition-colors duration-300 block">
                {n.title}
              </span>
              <span className="mt-1 block numeric-index text-[color:var(--ink-soft)]">
                {n.source}{n.published ? ` · ${timeAgo(n.published)}` : ""}
              </span>
            </span>
            <ExternalLink size={13} className="shrink-0 text-[color:var(--sepia)]/60 group-hover:text-[color:var(--rust)] transition-colors duration-300" />
          </a>
        </li>
      ))}
    </ol>
  );
}

export default function Local() {
  const { place, detecting } = usePlace();
  const { data, loading } = useLocalBundle(place);
  const news = data?.news || [];

  return (
    <main data-testid="local-page" className="pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Your corner of the world</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(44px, 7vw, 120px)" }}
        >
          {place?.city ? (
            <>
              {place.city}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">, where your story lives.</span>
            </>
          ) : (
            <>
              Where in the world <span className="font-italic-serif italic text-[color:var(--ink-soft)]">are you?</span>
            </>
          )}
        </motion.h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-[1.6] text-[color:var(--ink-soft)]">
          Every family archive is rooted in a place. Here is yours — its weather today,
          its headlines this week, and the story of the ground beneath it.
        </p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <LocationBar />
      </section>

      {place && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <WeatherPanel weather={data?.weather} place={place} loading={loading} />
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ArticlePanel article={data?.article} place={place} loading={loading} />
          </div>
        </section>
      )}

      {place && (news.length > 0 || loading) && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24" data-testid="local-news-section">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
              <Newspaper size={20} className="text-[color:var(--sepia)]" />
              The {place.city} dispatch
            </h2>
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          </div>
          {loading && news.length === 0 ? (
            <div className="py-10 text-center text-[color:var(--ink-soft)]">
              <Loader2 size={18} className="animate-spin inline mr-2" /> Gathering the headlines…
            </div>
          ) : (
            <NewsList news={news} />
          )}
          <div className="mt-4 numeric-index text-[color:var(--ink-soft)]">Headlines via Google News · each opens the original story</div>
        </section>
      )}

      {/* Heritage tie-in */}
      <section className="border-t border-[color:var(--sepia)]/20 bg-[color:var(--parchment-2)]/40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 text-center">
          <div className="flourish w-56 mx-auto mb-8" />
          <h2 className="heritage-title text-3xl md:text-5xl leading-tight">
            {place?.city ? `${place.city} is where your archive begins.` : "Your archive begins somewhere."}
          </h2>
          <p className="mt-4 max-w-xl mx-auto font-italic-serif italic text-lg text-[color:var(--ink-soft)]">
            Trace the families who walked these streets before you — then write your own chapter.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/app/register" data-testid="local-cta-register" className="btn-wood">Begin your archive</Link>
            <Link to="/family-trees" data-testid="local-cta-trees" className="btn-ghost">Explore family trees</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
